import { AbstractService } from '@uniqoders/sdk';
import TourDayItem from '@/api/objects/TourDayItem';
import TourDay from '@/api/objects/TourDay';
import Template from '@/api/objects/Template';

export default class ActivityService extends AbstractService {

   /**
   * Retrieves all agency Templates paginated
   * @protected
   * @param agency
   * @param params
   * @param paginate
   */
   public allPaginated(agency: string, params: any, paginate = true): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/activity/list', { agency }),
      params,
      options: {
        paginate,
      },
    });
  }

  /**
   * Creates a empty Activity
   * @protected
   * @param agency
   * @param data
   */
  public createEmptyActivity(agency: string, data: any): Promise<Template> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/activity/empty', { agency }),
      data,
    });
  }

  /**
   * Updates the specified Activity
   * @param agency
   * @param template
   * @param data
   * @protected
   */
  public updateActivity(agency: string, template: string | number, data: Record<string, any>): Promise<Template> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/activity/{template}', { agency, template }),
      data,
    });
  }

  /**
   * Retrieves a specific Activity
   * @param agency
   * @param template
   * @protected
   */
  public retrieveActivity(agency: string, template: number): Promise<Template> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/activity/{template}', { agency, template }),
    });
  }

  /**
   * Retrieves a specific Activity with all related data for CMS
   * @param agency
   * @param activityId
   * @protected
   */
  public retrieveActivityWithDetails(agency: string, activityId: number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/activity/{activityId}/cms', { agency, activityId }),
    });
  }

  /**
   * Retrieves a specific Activity Day Item
   * @param agency
   * @param template
   * @param templateDay
   * @param templateDayItem
   * @protected
   */
  public retrieveActivityDays(
    agency: string,
    template: number): Promise<TourDayItem> {
    return this.request({
      method: 'get',
          url: this.buildPath('/v2/{agency}/activity/days/{template}',
        {
          agency,
          template
        }),
    });
  }

  /**
   * Updates the specified Tour Day
   * @param agency
   * @param template
   * @param tourDay
   * @param data
   * @protected
   */
  public updateTourDay(
    agency: string,
    template: string | number,
    tourDay: string | number,
    data: Record<string, any>): Promise<TourDay> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/activity/{template}/day/{tourDay}', {
        agency,
        template,
        tourDay,
      }),
      data,
    });
  }

  /**
   * Deletes the specified Tour Day
   * @protected
   * @param agency
   * @param template
   * @param tourDay
   */
  public deleteTourDay(agency: string, template: number, tourDay: number): Promise<TourDay> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/activity/{template}/day/{tourDay}', {
        agency,
        template,
        tourDay,
      }),
    });
  }

  /**
   * Creates a single Activity Day Item
   * @protected
   * @param agency
   * @param template
   * @param tourDay
   * @param data
   */
  public createDayItem(
    agency: string,
    template: number,
    tourDay: number,
    data: Record<string, any>): Promise<TourDayItem> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/activity/{template}/day/{tourDay}/item',
        {
          agency,
          template,
          tourDay,
        }),
      data,
    });
  }

  /**
   * Updates a single Activity Day Item
   * @protected
   * @param agency
   * @param template
   * @param tourDay
   * @param tourDayItem
   * @param data
   */
  public updateDayItem(
    agency: string,
    template: number,
    tourDay: number,
    tourDayItem: string | number,
    data: Record<string, any>): Promise<TourDayItem> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/activity/{template}/day/{tourDay}/item/{tourDayItem}',
        {
          agency,
          template,
          tourDay,
          tourDayItem,
        }),
      data,
    });
  }

   /**
   * Retrieves a specific Activity Day Item
   * @param agency
   * @param template
   * @param templateDay
   * @param templateDayItem
   * @protected
   */
   public retrieveActivityDayItem(
    agency: string,
    template: number,
    templateDay: number,
    templateDayItem: number): Promise<TourDayItem> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/activity/{template}/day/{templateDay}/item/{templateDayItem}',
        {
          agency,
          template,
          templateDay,
          templateDayItem,
        }),
    });
  }

  /**
   * Deletes the specified Activity Day Item
   * @protected
   * @param agency
   * @param template
   * @param tourDay
   * @param tourDayItem
   */
  public deleteActivityDayItem(
    agency: string,
    template: number,
    tourDay: number,
    tourDayItem: number): Promise<TourDayItem> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/activity/{template}/day/{tourDay}/item/{tourDayItem}', {
        agency,
        template,
        tourDay,
        tourDayItem,
      }),
    });
  }

  /**
   * Changes the position of the specified Activity Day Item
   * @protected
   * @param agency
   * @param template
   * @param tourDay
   * @param tourDayItem
   * @param newPosition
   */
  public moveActivityDayItem(
    agency: string,
    template: number,
    tourDay: number,
    tourDayItem: number,
    newPosition: number): Promise<TourDayItem> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/activity/{template}/day/{tourDay}/item/{tourDayItem}/move/{newPosition}', {
        agency,
        template,
        tourDay,
        tourDayItem,
        newPosition
      }),
    });
  }

  /**
   * Archives the specified Activity
   * @param agency
   * @param template
   * @protected
   */
  public archiveActivity(agency: string, template: string | number): Promise<Template> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/activity/{template}', { agency, template }),
    });
  }

  /**
   * Duplicates the specified Activity
   * @param agency
   * @param template
   * @protected
   */
  public duplicateActivity(agency: string, template: string | number): Promise<Template> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/activity/{template}/duplicate', { agency, template }),
    });
  }

  /**
   * Creates a new Activity Day (V2)
   * @param agency
   * @param activityId
   * @param data
   */
  public createDay(
    agency: string,
    activityId: number,
    data: Record<string, any>
  ): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/activity/{activityId}/day', {
        agency,
        activityId,
      }),
      data,
    });
  }

  /**
   * Attach agencies
   * @protected
   * @param template
   * @param data
   */
  public attachAgencies(template: string | number, data: Record<string, any>): Promise<Template> {
    return this.request({
      method: 'post',
      url: this.buildPath('/activity/{template}/attach/agencies', { template }),
      data,
    });
  }

  /**
   * Detach agencies
   * @protected
   * @param template
   * @param data
   */
  public detachAgencies(template: string | number, data: Record<string, any>): Promise<Template> {
    return this.request({
      method: 'post',
      url: this.buildPath('/activity/{template}/detach/agencies', { template }),
      data,
    });
  }

  /**
   * Creates a single Activity Day Item Resource (V2)
   * @param agency
   * @param activity
   * @param activityDay
   * @param activityDayItem
   * @param data
   */
  public createActivityDayItemResource(
    agency: string,
    activity: number,
    activityDay: number,
    activityDayItem: number,
    data: Record<string, any>
  ): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/activity/{activity}/day/{activityDay}/item/{activityDayItem}/resource', {
        agency,
        activity,
        activityDay,
        activityDayItem,
      }),
      data,
    });
  }

  /**
   * Updates a single Activity Day Item Resource (V2)
   * @param agency
   * @param activity
   * @param activityDay
   * @param activityDayItem
   * @param activityDayItemResource
   * @param data
   */
  public updateActivityDayItemResource(
    agency: string,
    activity: number,
    activityDay: number,
    activityDayItem: number,
    activityDayItemResource: number,
    data: Record<string, any>
  ): Promise<any> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/activity/{activity}/day/{activityDay}/item/{activityDayItem}/resource/{activityDayItemResource}', {
        agency,
        activity,
        activityDay,
        activityDayItem,
        activityDayItemResource,
      }),
      data,
    });
  }

  /**
   * Deletes a single Activity Day Item Resource (V2)
   * @param agency
   * @param activity
   * @param activityDay
   * @param activityDayItem
   * @param activityDayItemResource
   */
  public deleteActivityDayItemResource(
    agency: string,
    activity: number,
    activityDay: number,
    activityDayItem: number,
    activityDayItemResource: number
  ): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/activity/{activity}/day/{activityDay}/item/{activityDayItem}/resource/{activityDayItemResource}', {
        agency,
        activity,
        activityDay,
        activityDayItem,
        activityDayItemResource,
      }),
    });
  }

  /**
   * Updates the meta for a specific Activity
   * @param agency
   * @param activityId
   * @param data
   */
  public updateActivityMeta(agency: string, activityId: string | number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/activity/{activityId}/meta', { agency, activityId }),
      data,
    });
  }

  /**
   * Retrieves CMS activity by activity ID
   * @param agency
   * @param activityId
   */
  public getCmsActivityByActivityId(agency: string, activityId: number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/cms/activities', { agency }),
      params: {
        filters: {
          activity_id: activityId
        }
      },
    });
  }

  public listCmsActivities(agency: string, params: Record<string, any> = {}): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/cms/activities', { agency }),
      params,
    });
  }

  /**
   * Retrieves a specific CMS activity
   * @param agency
   * @param cmsActivityId
   */
  public getCmsActivity(agency: string, cmsActivityId: number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/cms/activities/{cmsActivityId}', { agency, cmsActivityId }),
    });
  }

  /**
   * Creates a new CMS activity
   * @param agency
   * @param data
   */
  public createCmsActivity(agency: string, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/cms/activities', { agency }),
      data,
    });
  }

  /**
   * Updates a CMS activity
   * @param agency
   * @param cmsActivityId
   * @param data
   */
  public updateCmsActivity(agency: string, cmsActivityId: number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/v2/{agency}/cms/activities/{cmsActivityId}', { agency, cmsActivityId }),
      data,
    });
  }

  /**
   * Publishes a CMS activity
   * @param agency
   * @param cmsActivityId
   */
  public publishCmsActivity(agency: string, cmsActivityId: number, isPublic = true): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/cms/activities/{cmsActivityId}/publish', { agency, cmsActivityId }),
      data: {
        is_public: isPublic,
      },
    });
  }

  /**
   * Retrieves a CMS activity with full details using the underlying activity ID
   * @param agency
    * @param activityId
    */
  public getDetailedCmsActivityByActivityId(agency: string, activityId: number): Promise<any> {
    return this.request({
      method: 'get',
      url: this.buildPath('/v2/{agency}/cms/activities/by-activity/{activityId}', { agency, activityId }),
    });
  }

  public deleteCmsActivity(agency: string, cmsActivityId: number): Promise<any> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/v2/{agency}/cms/activities/{cmsActivityId}', { agency, cmsActivityId }),
    });
  }

  /**
   * Uploads or updates the featured image for a CMS activity
   * @param agency
   * @param cmsActivityId
   * @param data
   */
  public updateCmsActivityFeatured(agency: string, cmsActivityId: number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/cms/activities/{cmsActivityId}/featured', { agency, cmsActivityId }),
      data,
    });
  }

  /**
   * Uploads or updates the gallery images for a CMS activity
   * @param agency
   * @param cmsActivityId
   * @param data
   */
  public updateCmsActivityGallery(agency: string, cmsActivityId: number, data: Record<string, any>): Promise<any> {
    return this.request({
      method: 'post',
      url: this.buildPath('/v2/{agency}/cms/activities/{cmsActivityId}/gallery', { agency, cmsActivityId }),
      data,
    });
  }
}